import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactPaginate from "react-paginate";
import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Media,
  Row,
  Spinner,
  Table,
  Badge,
} from "reactstrap";
import OnlyHeader from "components/Headers/OnlyHeader";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import EditKnowledgeArticleModal from "components/Modals/chatbotModals/EditKnowledgeArticleModal";
import AddKnowledgeArticleModal from "components/Modals/chatbotModals/AddKnowledgeArticleModal";
import ChatbotSettingsModal from "components/Modals/chatbotModals/ChatbotSettingsModal";
import {
  getAllKnowledgeArticles,
  deleteKnowledgeArticle,
} from "store/actions/chatbotKnowledgeAction";
import { CATEGORIES } from "./KnowledgeArticleForm";

const KnowledgeBaseList = () => {
  const dispatch = useDispatch();

  const { articles, totalPages, getArticlesLoader } = useSelector(
    (state) => state.chatbotKnowledge
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [message, setMessage] = useState("");
  const [articleID, setArticleID] = useState("");

  // Edit modal state
  const [editModal, setEditModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Add modal state
  const [addModal, setAddModal] = useState(false);

  // Settings modal state
  const [settingsModal, setSettingsModal] = useState(false);

  const handleEditModal = (article) => {
    setSelectedArticle(article);
    setEditModal(true);
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (editModal) setSelectedArticle(null);
  };

  const toggleConfirmationModal = () => {
    setConfirmationModal(!confirmationModal);
  };

  const handleDeleteModal = (id) => {
    setArticleID(id);
    setMessage("Are you sure you want to delete this knowledge article?");
    toggleConfirmationModal();
  };

  const handleConfirmationOk = () => {
    dispatch(
      deleteKnowledgeArticle(articleID, () => {
        dispatch(
          getAllKnowledgeArticles({
            category: selectedCategory,
            status: selectedStatus,
            search: appliedSearch,
            page: currentPage,
            limit: itemsPerPage,
          })
        );
      })
    );
    toggleConfirmationModal();
  };

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    setAppliedSearch(searchTerm.trim());
    dispatch(
      getAllKnowledgeArticles({
        category: selectedCategory,
        status: selectedStatus,
        search: searchTerm.trim(),
        page: 1,
        limit: itemsPerPage,
      })
    );
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setSelectedCategory("");
    setSelectedStatus("");
    setCurrentPage(1);
    dispatch(
      getAllKnowledgeArticles({
        category: "",
        status: "",
        search: "",
        page: 1,
        limit: itemsPerPage,
      })
    );
  };

  useEffect(() => {
    dispatch(
      getAllKnowledgeArticles({
        category: selectedCategory,
        status: selectedStatus,
        search: appliedSearch,
        page: currentPage,
        limit: itemsPerPage,
      })
    );
  }, [dispatch, currentPage, itemsPerPage, selectedCategory, selectedStatus]);

  const isFiltersClear =
    !searchTerm.trim() &&
    !appliedSearch &&
    !selectedCategory &&
    !selectedStatus;

  const getCategoryLabel = (catValue) => {
    const found = CATEGORIES.find((c) => c.value === catValue);
    return found ? found.label : catValue || "-";
  };

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="d-flex flex-column justify-content-between align-items-center flex-xl-row">
                  <Col className="col-12 col-xl-auto mb-2 mb-xl-0">
                    <h3 className="text-center text-xl-left mb-0">
                      Chatbot Knowledge
                    </h3>
                  </Col>

                  <Col className="col-12 col-xl-auto d-flex flex-column align-items-center align-items-xl-end mt-2 mt-xl-0">
                    {/* Filters Row */}
                    <div className="d-flex flex-column flex-sm-row align-items-center justify-content-center justify-content-xl-end w-100 w-xl-auto">
                      {/* Search Input */}
                      <div className="w-100 w-sm-auto mb-2 mb-sm-0 mr-sm-2" style={{ minWidth: "180px", maxWidth: "220px" }}>
                        <FormGroup className="mb-0 w-100">
                          <Input
                            id="chatbotSearch"
                            name="Search"
                            placeholder="Search Here..."
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSearchSubmit();
                              }
                            }}
                          />
                        </FormGroup>
                      </div>

                      {/* Category Filter */}
                      <div className="w-100 w-sm-auto mb-2 mb-sm-0 mr-sm-2" style={{ minWidth: "145px" }}>
                        <FormGroup className="mb-0 w-100">
                          <Input
                            type="select"
                            name="category"
                            value={selectedCategory}
                            onChange={(e) => {
                              setSelectedCategory(e.target.value);
                              setCurrentPage(1);
                            }}
                          >
                            <option value="">All Categories</option>
                            {CATEGORIES.map((cat) => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </div>

                      {/* Status Filter */}
                      <div className="w-100 w-sm-auto mb-2 mb-sm-0" style={{ minWidth: "130px" }}>
                        <FormGroup className="mb-0 w-100">
                          <Input
                            type="select"
                            name="status"
                            value={selectedStatus}
                            onChange={(e) => {
                              setSelectedStatus(e.target.value);
                              setCurrentPage(1);
                            }}
                          >
                            <option value="">All Status</option>
                            <option value="published">Published</option>
                            <option value="draft">Draft</option>
                          </Input>
                        </FormGroup>
                      </div>
                    </div>

                    {/* Buttons Row */}
                    <div className="w-100 w-xl-auto mt-2 pt-1">
                      <Row className="no-gutters justify-content-center justify-content-xl-end">
                        <Col xs="6" sm="auto" className="pr-1 pr-sm-0 pb-2 pb-sm-0 mr-sm-2">
                          <Button
                            color="primary"
                            className="px-3 py-2 text-nowrap w-100"
                            style={{ minWidth: "115px" }}
                            disabled={getArticlesLoader}
                            onClick={handleSearchSubmit}
                            size="sm"
                          >
                            Search
                          </Button>
                        </Col>
                        <Col xs="6" sm="auto" className="pl-1 pl-sm-0 pb-2 pb-sm-0 mr-sm-2">
                          <Button
                            color="primary"
                            className="px-3 py-2 text-nowrap w-100"
                            style={{ minWidth: "115px" }}
                            disabled={getArticlesLoader || isFiltersClear}
                            onClick={handleClearSearch}
                            size="sm"
                          >
                            Clear Search
                          </Button>
                        </Col>
                        <Col xs="6" sm="auto" className="pr-1 pr-sm-0 pb-2 pb-sm-0 mr-sm-2">
                          <Button
                            color="primary"
                            className="px-3 py-2 text-nowrap w-100"
                            style={{ minWidth: "115px" }}
                            size="sm"
                            onClick={() => setAddModal(true)}
                          >
                            Add Article
                          </Button>
                        </Col>
                        <Col xs="6" sm="auto" className="pl-1 pl-sm-0 pb-2 pb-sm-0">
                          <Button
                            color="secondary"
                            className="px-3 py-2 text-nowrap w-100"
                            style={{ minWidth: "115px" }}
                            size="sm"
                            onClick={() => setSettingsModal(true)}
                            title="Configure escalation email settings"
                          >
                            <i className="ni ni-settings-gear-65 mr-1" />
                            Settings
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </CardHeader>

              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Title</th>
                    <th scope="col">Category</th>
                    <th scope="col">Status</th>
                    <th scope="col" style={{ width: "220px" }}>Keywords</th>
                    <th scope="col">Updated At</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>

                {!getArticlesLoader ? (
                  <tbody>
                    {articles && articles.length > 0 ? (
                      articles.map((article, index) => {
                        const id = article?._id || article?.id;
                        const isPublished = article?.status === "published";

                        return (
                          <tr key={id || index}>
                            <th scope="row">
                              <Media className="align-items-center">
                                <Media>
                                  <span className="mb-0 text-sm font-weight-bold">
                                    {article?.title}
                                  </span>
                                </Media>
                              </Media>
                            </th>

                            <td className="text-sm">
                              {getCategoryLabel(article?.category)}
                            </td>

                            <td>
                              <Badge
                                color={isPublished ? "success" : "warning"}
                                pill
                                className="p-1 px-2"
                              >
                                {isPublished ? "Published" : "Draft"}
                              </Badge>
                            </td>

                            <td style={{ maxWidth: "200px" }}>
                              <div
                                className="text-truncate"
                                title={
                                  article?.keywords && article.keywords.length > 0
                                    ? article.keywords.join(", ")
                                    : ""
                                }
                              >
                                {article?.keywords && article.keywords.length > 0
                                  ? article.keywords.join(", ")
                                  : "-"}
                              </div>
                            </td>

                            <td>{formatTimestamp(article?.updatedAt || article?.createdAt)}</td>

                            <td>
                              <div className="d-flex align-items-center">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleEditModal(article)}
                                >
                                  <i className="fas fa-pencil-alt" />
                                </Button>
                                <Button
                                  color="danger"
                                  className="mx-2"
                                  size="sm"
                                  onClick={() => handleDeleteModal(id)}
                                >
                                  <i className="fa fa-trash"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center" }}>
                          <p className="mb-0 text-sm py-4">No Article Found.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center" }}>
                        <Spinner
                          size="lg"
                          style={{ display: "block", margin: "20px auto" }}
                        />
                      </td>
                    </tr>
                  </tbody>
                )}
              </Table>

              {totalPages > 0 && (
                <CardFooter className="py-4">
                  <nav aria-label="...">
                    <ReactPaginate
                      previousLabel={"<"}
                      nextLabel={">"}
                      breakLabel={"..."}
                      breakClassName={"break-me"}
                      pageCount={totalPages}
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={2}
                      onPageChange={handlePageChange}
                      containerClassName={
                        "pagination px-1 justify-content-end mb-0"
                      }
                      activeClassName={"active"}
                      pageClassName={"page-item"}
                      pageLinkClassName={"page-link"}
                      previousClassName={"page-item"}
                      nextClassName={"page-item"}
                      previousLinkClassName={"page-link"}
                      nextLinkClassName={"page-link"}
                      forcePage={currentPage - 1}
                    />
                  </nav>
                </CardFooter>
              )}
            </Card>
          </div>
        </Row>

        <EditKnowledgeArticleModal
          modal={editModal}
          toggle={toggleEditModal}
          article={selectedArticle}
          onSuccess={() => {
            dispatch(
              getAllKnowledgeArticles({
                category: selectedCategory,
                status: selectedStatus,
                search: appliedSearch,
                page: currentPage,
                limit: itemsPerPage,
              })
            );
          }}
        />

        <AddKnowledgeArticleModal
          modal={addModal}
          toggle={() => setAddModal(!addModal)}
          onSuccess={() => {
            dispatch(
              getAllKnowledgeArticles({
                category: selectedCategory,
                status: selectedStatus,
                search: appliedSearch,
                page: 1,
                limit: itemsPerPage,
              })
            );
          }}
        />

        <ChatbotSettingsModal
          modal={settingsModal}
          toggle={() => setSettingsModal(!settingsModal)}
        />

        <ConfirmationModal
          modal={confirmationModal}
          toggle={toggleConfirmationModal}
          userId={articleID}
          message={message}
          onConfirm={handleConfirmationOk}
          onCancel={toggleConfirmationModal}
        />
      </Container>
    </>
  );
};

export default KnowledgeBaseList;
